<template>
  <div v-if="!isLoading" class="team-invite-page">
    <div class="container maxWidth-sm form-inline" v-if="model">
      <h1 class="text-center text-wrap" style="margin-top: 40px">
        Invite team members to <br />'{{ name }}'
      </h1>
      <hr />

      <TeamInviteControl
        v-if="!isLicenceHolder || canInvite"
        :model="model"
        @deleted-event="loadInviteModel"
      />

      <!-- CONTINUE/MANAGE BUTTON -->
      <div class="row" style="margin-top: 20px">
        <div class="col-sm-12 text-center">
          <a class="btn btn-lg btn-default" :href="continueButtonUrl"> Continue </a>
        </div>
      </div>
      <!-- END OF CONTINUE/MANAGE BUTTON -->

      <!-- NO PERMISSIONS UI -->
      <div v-if="isLicenceHolder && !canInvite" class="col-sm-10 col-sm-offset-1 text-big">
        <p>You do not have permission to invite to this team</p>
      </div>
      <!-- END OF NO PERMISSIONS UI -->

      <div style="margin-top: 50px"></div>
    </div>
  </div>
  <LoadingPage v-else />
</template>

<script setup lang="ts">
import { debug as d } from "debug";
import axios, { AxiosError } from "axios";
import { computed, onBeforeMount, ref } from "vue";
import { InviteControlApiModel, TeamSettingsApiModel } from "../../api-models";
import TeamInviteControl from "../../components/invite-control/team-invite-control.vue";
import { csContext } from "../../services/cs-context";
import LoadingPage from "../loading-page.vue";

const debug = d("cs.TeamInvite");

export interface GroupInviteModel {
  groupName: string;
  invites: InviteControlApiModel;
}

const props = defineProps<{ id: string }>();
const name = ref<string | null>(null);
const model = ref<InviteControlApiModel | null>(null);
const teamSettingsModel = ref<TeamSettingsApiModel | null>(null);
const isLoading = ref(false);

const canInvite = computed(() => !!teamSettingsModel.value?.userPermissions.canInviteToGroup);
const isLicenceHolder = computed(() => csContext.userProfile.hasLicence);
const continueButtonUrl = computed(() => `/home?view=TeamDashboard&team=${props.id}`);

async function loadInviteModel(): Promise<void> {
  return axios
    .get(`/team/invitemodel/${props.id}`)
    .then((resp) => {
      const inviteModel = resp.data as GroupInviteModel;
      name.value = inviteModel.groupName;
      const controlModel = inviteModel.invites;
      controlModel.sendUrl = `/invitecodes/sendgroupinvites/${props.id}`;
      controlModel.peoplePhrase = inviteModel.groupName;
      debug(inviteModel.groupName);
      model.value = controlModel;
    })
    .catch((e: AxiosError) => {
      console.error(e);
    });
}

function loadData(): void {
  const axiosPromises: Promise<void>[] = [];
  axiosPromises.push(loadInviteModel());
  axiosPromises.push(
    axios
      .get(`/api/teamsettings/${props.id}`)
      .then((resp) => {
        teamSettingsModel.value = resp.data as TeamSettingsApiModel;
      })
      .catch((e: AxiosError) => {
        if (e.response?.status === 403) {
          window.location.href = "/errorpages/NotAuthorised";
        } else if (e.response?.status === 404) {
          window.location.href = "/errorpages/NotFound";
        } else {
          window.location.href = "/errorpages/Error";
        }
      }),
  );

  Promise.all(axiosPromises).then(() => {
    isLoading.value = false;
  });
}

onBeforeMount(() => {
  isLoading.value = true;
  loadData();
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less" scoped>
@import (reference) "../../styles/bootstrap/variables.less";

.org-admin-list {
  padding: 20px 0;

  a {
    font-weight: bold;
  }
}

h3 {
  text-align: center;
}

.text-wrap {
  overflow-wrap: break-word;
}

.no-seats {
  text-align: center;

  h3 {
    color: @dark-background-color;
  }
}

hr {
  border-top: 2px solid @charismatic-color;
  margin-top: 5px;
}

.team-invite-image {
  position: relative;
  left: 10%;
  width: 70%;
}

@media screen and (max-width: 840px) {
  hr {
    margin-left: 0px;
    margin-right: 0px;
  }
}
</style>
